import React, { Fragment, useRef, useContext } from 'react';
import _merge from 'lodash/merge';
import ArrowTriangleDownIcon from '@nokia-csf-uxr/ccfk-assets/ArrowTriangleDownIcon';
import KEY_CODES from 'common/KeyCodes';
import { getContext, useCombinedRefs } from 'common';
import useDidUpdateEffect from 'common/useDidUpdateEffect';
import { SelectItemSearchableContext } from './SelectItemSearchable';
import Styled from './styles';

const {
  ENTER_KEY,
} = KEY_CODES;

export interface SelectItemInputProps extends Omit<React.ComponentPropsWithRef<'input'>, 'placeholder'> {
  /** The element to use as the dropdown icon (usually a down arrow icon). */
  dropdownIcon?: React.ReactNode;
  /** The value will appear in the SelectItem base area. Note: The native input element's `value` attribute will be set using this prop. */
  value?: string;
  /** The short hint displayed in the SelectItem base area when no item has been selected. Note: The native input element's `placeholder` attribute will be set using this prop. */
  placeholder?: string | React.ReactNode;
  /**
   * Callback fired when the value is changed. Signature: `function(event: object) => void`, event: The event source of the callback.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * Callback fired when the input is submitted via keyboard ENTER key. Signature: `function(event: object) => void`, event: The event source of the callback.
   */
  onSubmit?: React.FormEventHandler;
  /** Renders a Button onto the SelectItem; can be used to clear the input value. Consider using SelectItemClearButton. */
  renderClearButton?(...args: unknown[]): React.ReactNode;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** SelectItemInput is a component to render the item(s) selected from the dropdown List and collect input from the user typing in the
 * SelectItem base area.  Any other props than the `SelectItemInput` props will be applied to the internal `input` element. */
const SelectItemInput = React.forwardRef<HTMLElement, SelectItemInputProps>((props: SelectItemInputProps, ref) => getContext(({ RTL, theme }) => {
  const {
    dropdownIcon,
    value,
    placeholder,
    onChange,
    onSubmit,
    renderClearButton,
    ...otherProps
  } = props;
  const inputEl = useRef(null);
  const combinedInputRef = useCombinedRefs(ref, inputEl);
  const selectItemSearchableContext = useContext(SelectItemSearchableContext);

  // move cursor to beginning of input element and select all text making it easier
  // for user to delete it
  const positionCursorAtBeginning = () => {
    if (selectItemSearchableContext.disabled) return;
    if (inputEl && inputEl.current && inputEl.current.setSelectionRange) {
      inputEl.current.setSelectionRange(0, 0);
      inputEl.current.select();
    } else if (inputEl && inputEl.current && inputEl.current.createTextRange) {
      const range = inputEl.current.createTextRange();
      range.move('character', 0);
      range.select();
    }
  };

  const focusInput = () => {
    !selectItemSearchableContext.disabled && inputEl && inputEl.current && inputEl.current.focus();
  };

  // only want these effects after initial render
  useDidUpdateEffect(!selectItemSearchableContext.disabled && !selectItemSearchableContext.isOpen ? positionCursorAtBeginning : undefined, [value, selectItemSearchableContext.isOpen]);
  useDidUpdateEffect(!selectItemSearchableContext.disabled && !selectItemSearchableContext.isOpen && !selectItemSearchableContext.clickedOutside ? focusInput : undefined, [selectItemSearchableContext.isOpen]);

  const handleFocus = (event) => {
    positionCursorAtBeginning();
    const { onFocus } = props;
    onFocus && onFocus(event);
  };

  const handleKeyDown = (event) => {
    const { key } = event;
    if (key === ENTER_KEY) {
      onSubmit && onSubmit(event);
    }
    const { onKeyDown } = props;
    onKeyDown && onKeyDown(event);
  };

  const globalTextInputTheme = selectItemSearchableContext.variant === 'outlined' ?
    theme.TEXT_INPUT.OUTLINED :
    theme.TEXT_INPUT.UNDERLINED;
  const resolvedTheme = _merge({}, globalTextInputTheme, selectItemSearchableContext.resolvedTextInputTheme);
  const hideClearButton =
    selectItemSearchableContext.disabled || renderClearButton === undefined;
  return (
    <Fragment>
      <Styled.SelectItemInput
        isOpen={selectItemSearchableContext.isOpen}
        ref={combinedInputRef}
        RTL={RTL}
        theme={resolvedTheme}
        tabIndex={!selectItemSearchableContext.disabled ? 0 : -1}
        variant={selectItemSearchableContext.variant}
        type="text"
        role="combobox"
        aria-expanded={selectItemSearchableContext.ariaExpanded}
        aria-autocomplete="list"
        aria-controls={selectItemSearchableContext.ariaControls}
        aria-owns={selectItemSearchableContext.ariaOwns}
        placeholder={placeholder}
        $isplaceholder={placeholder !== undefined}
        disabled={selectItemSearchableContext.disabled}
        onChange={onChange}
        value={value}
        autoFocus={selectItemSearchableContext.autoFocus}
        {...otherProps}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      <Styled.SelectIndicators RTL={RTL}>
        <Styled.DropDownIndicator
          disabled={selectItemSearchableContext.disabled}
          $isOpen={selectItemSearchableContext.isOpen && !selectItemSearchableContext.disabled}
          theme={resolvedTheme}
          hide={!hideClearButton}
          tabIndex={-1}
        >
          {dropdownIcon}
        </Styled.DropDownIndicator>
        <Styled.ClearButton RTL={RTL} disabled={selectItemSearchableContext.disabled} hide={hideClearButton}>
          {renderClearButton && renderClearButton()}
        </Styled.ClearButton>
      </Styled.SelectIndicators>
    </Fragment>
  );
}));

SelectItemInput.displayName = 'SelectItemInput';

SelectItemInput.defaultProps = {
  dropdownIcon: <ArrowTriangleDownIcon aria-label={null} />,
  value: undefined,
  placeholder: undefined,
  onChange: undefined,
  onSubmit: undefined,
  renderClearButton: undefined,
  onFocus: undefined,
  onKeyDown: undefined
};

export {
  SelectItemInput as default,
};
